import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Loader2, ShieldCheck } from "lucide-react";
import { useCustomerAuth } from "../context/CustomerAuthContext";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[6-9]\d{9}$/;

export default function ForgotPassword() {
  const { forgotPassword } = useCustomerAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Cynos Forgot Password";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const value = identifier.trim();
    if (!value) {
      setError("Email or mobile number is required.");
      return;
    }

    const isEmail = value.includes("@");
    if (isEmail && !emailRegex.test(value)) {
      setError("Enter a valid email address.");
      return;
    }
    if (!isEmail && !phoneRegex.test(value)) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return;
    }

    setLoading(true);
    try {
      const data = await forgotPassword(value);
      const target = data?.delivery_target || "your registered email";
      setSuccess(`OTP sent to ${target}. Please check your inbox (and spam folder).`);
      setTimeout(() => navigate("/verify-otp", { replace: true }), 800);
    } catch (err) {
      const message = err.response?.data?.detail || err.message || "Unable to send OTP.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="mx-auto max-w-md rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-2xl shadow-black/20"
      >
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center rounded-full bg-cynos-red/10 p-4 mb-4">
            <ShieldCheck className="h-6 w-6 text-cynos-red" />
          </div>
          <h1 className="text-3xl font-anton uppercase text-white">Forgot Password</h1>
          <p className="mt-3 text-sm text-zinc-400">Enter your registered email or mobile to find your account.</p>
          <p className="mt-2 text-xs text-zinc-500">For security, the OTP will always be sent to your registered email address.</p>
        </div>

        {error && <div className="mb-6 rounded-2xl bg-red-500/15 px-4 py-3 text-sm text-red-200">{error}</div>}
        {success && <div className="mb-6 rounded-2xl bg-emerald-500/15 px-4 py-3 text-sm text-emerald-200">{success}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-zinc-400">Email or Mobile Number</label>
            <input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-black/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cynos-red"
              placeholder="customer@example.com or 9876543210"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cynos-red px-4 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Sending OTP" : "Send OTP"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-zinc-400">
          <Link to="/signin" className="underline hover:text-white">Back to Sign In</Link>
        </div>
      </motion.div>
    </main>
  );
}
